import moment, { Moment } from 'moment';
import { useState } from 'react';
import { RepeatKey, Space, SpaceBooking, SpaceGroup, SpaceSubscription } from '../../types';

import { AutoCompleteTextInput } from 'components/Form/AutoCompleteTextInput';
import { Form } from 'components/Form/base/Form';
import FormConsumser from 'components/Form/base/FormConsumer';
import { DateInput } from 'components/Form/DateInput';
import Loader from 'components/Loader';
import Flex from 'components/styledComponents/Flex';
import Joi from 'joi';

import FormInput from '@form/FormInput';
import { cx } from 'class-variance-authority';
import { SelectInput } from 'components/Form/SelectInput';
import { SubmitInput } from 'components/Form/SubmitInput';
import { MappedSpace } from 'components/SpaceBooking/SpaceTable/types';
import { timeDivisions } from 'components/TrainingPlan/TrainingPlanHelpers';
import useSWR from 'swr';
import { FormDivider } from '~/components/Form/next';
import { FellowsForm, SubmitWithPriceCalculator } from '.';
import { OverlappingAssistant, OverlappingAssistantElement } from '../OverlappingAssistant';

interface Props {
    isLoading?: boolean;
    editMode?: true;
    showFellows?: boolean;
    adminMode?: boolean;
    space?: MappedSpace;
    spaceGroup?: SpaceGroup;
    startTime?: number;
    startDate?: Moment;
    callback: (bookingData: {
        start: string;
        start_time: number;
        end_time: number;
        repeat_until: string;
        repeat_period: RepeatKey;
        spaces: string;
        calculatedPrice: number;
    }) => void;
    booking?: Partial<SpaceBooking>;
    clubSlug?: string;
    email?: string;
    subscription?: Partial<SpaceSubscription>;
}

export const BookingForm = ({
    isLoading,
    editMode,
    showFellows,
    adminMode,
    spaceGroup,
    space,
    startTime,
    startDate,
    callback,
    booking,
    clubSlug,
    email,
    subscription
}: Props) => {
    const [calculatedPrice, setCalculatedPrice] = useState(null);
    const { data, error } = useSWR<{ data: Space[] }>(
        spaceGroup || clubSlug ? `space/${spaceGroup?.id || clubSlug}` : null
    );

    const handleSubmit = (data) => {
        callback({ ...data, calculatedPrice });
    };

    const schema = Joi.object({
        start: Joi.date().required().label('Start'),
        start_time: Joi.number().required().label('Startzeit'),
        end_time: Joi.number()
            .required()
            .min(Joi.ref('start_time'))
            .message('Deine Startzeit muss vor deiner Endzeit sein')
            .label('Endzeit'),
        repeat_period: Joi.string().valid('', null, 'DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY').label('Wiederholen'),
        repeat_until: Joi.date().allow('', null).label('Wiederholen bis'),
        spaces: Joi.array()
            .required()
            .min(1)
            .items(Joi.object({ id: Joi.number().required().min(0) }).unknown())
            .label('Plätze')
    }).unknown();

    return (
        <Loader isLoading={!data && !error}>
            <Form
                options={{ mode: 'onChange' }}
                onSubmit={handleSubmit}
                validationSchema={schema}
                setDefaultValuesOnRerender={data === undefined}
                defaultValues={{
                    notify_user: true,
                    start: moment(startDate).toDate(),
                    start_time: startTime || 32,
                    end_time: startTime + 4 || 38,
                    spaces: data?.data?.filter((s) => s.slug === space?.slug) || data?.data?.[0],
                    ...(subscription
                        ? {
                              start: subscription?.start,
                              repeat_until: subscription?.end,
                              repeat_period: subscription?.repeat_period,
                              start_time: subscription?.start_time,
                              end_time: subscription?.end_time,
                              exemptions: subscription?.exemptions,
                              spaces: data?.data?.filter((s) => subscription?.spaces.includes(s.id))
                          }
                        : {}),
                    ...booking
                }}
            >
                <div className={cx('tw-grid tw-gap-6', adminMode && 'tw-grid-cols-2')}>
                    <div>
                        {adminMode && <FormDivider title="Buchungsdaten" />}
                        <DateInput name="start" label="Datum" />
                        <Flex className="mb-3" equal gap={0.5}>
                            <SelectInput
                                name="start_time"
                                label="Startzeit"
                                optionsWithKey={timeDivisions.times.map((time, index) => ({
                                    value: index,
                                    label: time
                                }))}
                            />
                            <SelectInput
                                name="end_time"
                                label="Endzeit"
                                optionsWithKey={timeDivisions.times.map((time, index) => ({
                                    value: index,
                                    label: time
                                }))}
                            />
                        </Flex>
                        <FormConsumser>
                            {({ watch }) => {
                                const repeat_period = watch('repeat_period');
                                const spaces = watch('spaces');

                                return (
                                    (!spaces?.some((s) => s.repeat_booking_guidelines?.includes('none')) ||
                                        adminMode) && (
                                        <Flex className="mb-3" equal gap={0.5}>
                                            <SelectInput name="repeat_period" label="Wiederholen">
                                                <option value="DAILY">täglich</option>
                                                <option value="WEEKLY">wöchentlich</option>
                                                <option value="MONTHLY">monatlich</option>
                                                <option value="YEARLY">jährlich</option>
                                            </SelectInput>

                                            {repeat_period && <DateInput name="repeat_until" label="Wiederholen bis" />}
                                        </Flex>
                                    )
                                );
                            }}
                        </FormConsumser>
                        <AutoCompleteTextInput
                            emptyLabel="Keine Plätze verfügbar."
                            data={data?.data}
                            dataKey="name"
                            name="spaces"
                            label="Plätze"
                            multiple
                        />
                        {!editMode || showFellows || adminMode ? (
                            <FellowsForm email={email} name="fellows" spaceGroup={spaceGroup} />
                        ) : null}
                        <hr />
                        {adminMode && (
                            <FormInput.Checkbox name="notify_user" label="Buchende über die Änderung beinachrichten" />
                        )}
                        {editMode ? (
                            <SubmitInput className="mt-3" isLoading={isLoading} block>
                                Speichern
                            </SubmitInput>
                        ) : (
                            <SubmitWithPriceCalculator
                                email={email}
                                price={calculatedPrice}
                                setPrice={setCalculatedPrice}
                            />
                        )}
                    </div>
                    {adminMode && (
                        <div>
                            <FormDivider title="Überlappungen" />
                            <FormConsumser>
                                {({ watch }) => {
                                    const keys = [
                                        'id',
                                        'start',
                                        'start_time',
                                        'end_time',
                                        'spaces',
                                        'repeat_period',
                                        'repeat_until',
                                        'exemptions'
                                    ];
                                    const data = watch(keys) as OverlappingAssistantElement;
                                    const mappedData = keys.reduce(
                                        (curr, key, index) => ({ ...curr, [key]: data[index] }),
                                        {}
                                    );

                                    return <OverlappingAssistant element={mappedData} />;
                                }}
                            </FormConsumser>
                        </div>
                    )}
                </div>
            </Form>
        </Loader>
    );
};
